import { KebabHorizontalIcon, TrashIcon } from '@primer/octicons-react';
import { GraphQLError } from 'graphql';
import { useRouter } from 'next/router';
import React from 'react';
import { graphql } from 'react-relay';

import BrandMessageTaskCreateDialog from '../../components/brandMessageTemplate/BrandMessageTaskCreateDialog';
import BrandMessageTemplateDescriptionList from '../../components/brandMessageTemplate/BrandMessageTemplateDescriptionList';
import BrandMessageTemplateUpdateDialog, {
  BrandMessageTemplateCloneDialog,
} from '../../components/brandMessageTemplate/BrandMessageTemplateUpdateDialog';
import ActionList from '../../components/core/ActionList';
import ActionMenu from '../../components/core/ActionMenu';
import Card from '../../components/core/Card';
import DialogButton from '../../components/core/DialogButton';
import Grid from '../../components/core/Grid';
import Head from '../../components/core/Head';
import HorizontalDivider from '../../components/core/HorizontalDivider';
import IconButton from '../../components/core/IconButton';
import { HeaderSidebarNavPageLayout } from '../../components/core/Layout';
import Stack from '../../components/core/Stack';
import Text from '../../components/core/Text';
import View from '../../components/core/View';
import useLazyLoadQuery from '../../hooks/useLazyLoadQuery';
import useToast from '../../hooks/useToast';
import { BrandMessageTemplateId_brandMessageTemplateDeleteMutation } from '../../relay/__generated__/BrandMessageTemplateId_brandMessageTemplateDeleteMutation.graphql';
import { BrandMessageTemplateId_brandMessageTemplateQuery } from '../../relay/__generated__/BrandMessageTemplateId_brandMessageTemplateQuery.graphql';
import { parseGraphQLError } from '../../utils/error';
import { NextPage } from '../_app';

const brandMessageTemplateQuery = graphql`
  query BrandMessageTemplateId_brandMessageTemplateQuery($id: ID!) {
    brandMessageTemplate(id: $id) {
      id

      ...BrandMessageTemplateDescriptionList_brandMessageTemplate
      ...BrandMessageTemplateUpdateDialog_brandMessageTemplate
      ...BrandMessageTaskCreateDialog_brandMessageTemplate

      templateName
      content

      chatBubbleType

      image {
        key
        size
      }
      imageLink

      buttons {
        name
        linkType
        linkIos
        linkMobile
        linkPc
        linkAndroid
      }
      coupon {
        description
        linkAndroid
        linkIos
        linkMobile
        linkPc
        title
      }
    }
  }
`;

type Props = {};

const BrandMessageTemplateId: NextPage<Props> = () => {
  const router = useRouter();
  const { toast } = useToast();

  const brandMessageTemplateId = router.query.brandMessageTemplateId as string;

  // 메시지 플래너가 이 화면으로 링크를 걸어 제작 모달을 바로 띄운다. 대상 SQL 은 길이가
  //   수백 KB 까지 가서 URL 로 못 넘기므로 플래너가 클립보드에 복사하고, 나머지만 채운다.
  const { createTask, title, description, startAt, period } = router.query;
  const taskInitialValues = {
    ...(title ? { title: title as string } : {}),
    ...(description ? { description: description as string } : {}),
    ...(startAt ? { startAt: startAt as string } : {}),
    ...(period ? { period: Number(period) } : {}),
  };

  const [{ brandMessageTemplate }] = useLazyLoadQuery<BrandMessageTemplateId_brandMessageTemplateQuery>(
    brandMessageTemplateQuery,
    {
      id: brandMessageTemplateId,
    },
  );

  if (!brandMessageTemplate) return null;

  const { id, templateName } = brandMessageTemplate;

  return (
    <View>
      <Head siteTitle={`브랜드 메세지 템플릿 | ${templateName}`} />
      <View>
        <Grid sx={{ alignItems: 'center' }}>
          <Grid.Unit size={'max'}>
            <Text as={'h1'}>{templateName}</Text>
          </Grid.Unit>
          <Grid.Unit size={'min'}>
            <Stack gapX={2}>
              <Stack.Item>
                <DialogButton
                  size={'large'}
                  renderDialog={({ isOpen, closeDialog }) => (
                    <BrandMessageTemplateCloneDialog
                      onDismiss={closeDialog}
                      isOpen={isOpen}
                      brandMessageTemplate={brandMessageTemplate}
                      wide
                      config={{
                        onCompleted: ({ brandMessageTemplateCreate: { id } }) => {
                          toast('복제가 완료됐어요', 'success');
                          router.push(`/brandMessageTemplate/${id}`);
                          closeDialog();
                        },
                        onError: () => {
                          toast('복제에 실패했어요', 'error');
                        },
                      }}
                    />
                  )}
                >
                  복제하기
                </DialogButton>
              </Stack.Item>
              <Stack.Item>
                <DialogButton
                  size={'large'}
                  renderDialog={({ isOpen, closeDialog }) => (
                    <BrandMessageTemplateUpdateDialog
                      onDismiss={closeDialog}
                      isOpen={isOpen}
                      brandMessageTemplate={brandMessageTemplate}
                      wide
                      config={{
                        onCompleted: () => {
                          toast('수정이 완료됐어요', 'success');

                          closeDialog();
                        },
                        onError: () => {
                          toast('수정에 실패했어요', 'error');
                        },
                      }}
                    />
                  )}
                >
                  수정하기
                </DialogButton>
              </Stack.Item>
              <Stack.Item>
                <DialogButton
                  size={'large'}
                  defaultOpen={createTask === '1'}
                  renderDialog={({ isOpen, closeDialog }) => (
                    <BrandMessageTaskCreateDialog
                      onDismiss={closeDialog}
                      isOpen={isOpen}
                      wide
                      brandMessageTemplate={brandMessageTemplate}
                      initialValues={taskInitialValues}
                      config={{
                        onCompleted: ({ brandMessageTaskCreate: { id } }) => {
                          toast('브랜드 메세지 제작이 완료됐어요', 'success');
                          router.push(`/brandMessageTask/${id}`);
                          closeDialog();
                        },
                        onError: () => {
                          toast('브랜드 메세지 제작에 실패했어요', 'error');
                        },
                      }}
                    />
                  )}
                  variant={'primary'}
                >
                  메세지 제작하기
                </DialogButton>
              </Stack.Item>
              <Stack.Item>
                <ActionMenu>
                  <ActionMenu.Anchor>
                    <IconButton icon={KebabHorizontalIcon} variant={'plain'} aria-label="Open column options" />
                  </ActionMenu.Anchor>
                  <ActionMenu.Overlay>
                    <ActionList>
                      <ActionList.MutationItem<BrandMessageTemplateId_brandMessageTemplateDeleteMutation>
                        mutation={graphql`
                          mutation BrandMessageTemplateId_brandMessageTemplateDeleteMutation(
                            $input: BrandMessageTemplateDeleteInput!
                          ) {
                            brandMessageTemplateDelete(input: $input) {
                              id @deleteRecord
                            }
                          }
                        `}
                        input={{ id }}
                        variant={'danger'}
                        config={{
                          onCompleted: () => {
                            router.replace('/brandMessageTemplate').then(() => toast('삭제가 완료됐어요', 'success'));
                          },
                          onError: (error) => {
                            toast(
                              parseGraphQLError(error as GraphQLError)?.[0].message || '삭제에 실패했어요',
                              'error',
                            );
                          },
                        }}
                        message={'정말 삭제할까요?'}
                      >
                        <ActionList.LeadingVisual>
                          <TrashIcon />
                        </ActionList.LeadingVisual>
                        삭제하기
                      </ActionList.MutationItem>
                    </ActionList>
                  </ActionMenu.Overlay>
                </ActionMenu>
              </Stack.Item>
            </Stack>
          </Grid.Unit>
        </Grid>
        <HorizontalDivider mt={[3, 3, 0]} mb={5} />
        <Grid gapX={5} gapY={3} reverse={[true, true, false]}>
          <Grid.Unit size={[1, 1, 3 / 4]}>
            <View sx={{ marginTop: 5 }}>
              <Card sx={{ padding: 4 }}>
                <BrandMessageTemplateDescriptionList
                  brandMessageTemplate={brandMessageTemplate}
                  titleUnitSize={1 / 4}
                  descriptionUnitSize={3 / 4}
                />
              </Card>
            </View>
            <View sx={{ marginTop: 5 }}>
              <Card sx={{ padding: 4 }}>
                <BrandMessageTemplateDescriptionList
                  type={'preview'}
                  brandMessageTemplate={brandMessageTemplate}
                  titleUnitSize={1 / 4}
                  descriptionUnitSize={3 / 4}
                />
              </Card>
            </View>
          </Grid.Unit>
          <Grid.Unit size={[1, 1, 1 / 4]}>
            <BrandMessageTemplateDescriptionList
              brandMessageTemplate={brandMessageTemplate}
              type={'activity'}
              titleUnitSize={'max'}
              descriptionUnitSize={'min'}
              renderTitle={(title) => <Text sx={{ fontSize: 1, fontWeight: 'bold' }}>{title}</Text>}
            />
          </Grid.Unit>
        </Grid>
      </View>
    </View>
  );
};

BrandMessageTemplateId.getLayout = (page) => <HeaderSidebarNavPageLayout>{page}</HeaderSidebarNavPageLayout>;
BrandMessageTemplateId.authenticated = true;
BrandMessageTemplateId.routes = [
  {
    id: 'brandMessageTemplateId',
    pathname: '/brandMessageTemplate/[brandMessageTemplateId]',
    name: '브랜드 메세지 템플릿 상세',
    permissions: ['app_push_read'],
  },
];

export default BrandMessageTemplateId;
